import React from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const schema = yup.object({
  name: yup
    .string()
    .min(1, 'マンション名は1文字以上で入力してください')
    .required('マンション名は必須です'),
  address: yup.string().optional(),
  postalCode: yup
    .string()
    .optional()
    .test('postal-code-format', '正しい郵便番号の形式で入力してください（例：123-4567）', function(value: string | undefined) {
      if (!value) return true; // 空文字の場合はOK
      return /^\d{3}-?\d{4}$/.test(value);
    }),
  totalUnits: yup
    .number()
    .optional()
    .test('positive-integer', '正の整数を入力してください', function(value: number | undefined) {
      if (value === undefined) return true;
      return Number.isInteger(value) && value > 0;
    }),
  fiscalYearStartMonth: yup
    .number()
    .optional()
    .test('month-range', '1-12の範囲で入力してください', function(value: number | undefined) {
      if (value === undefined) return true;
      return value >= 1 && value <= 12;
    }),
  managementCompany: yup.string().optional(),
  establishedDate: yup.string().optional(),
  contactEmail: yup
    .string()
    .optional()
    .email('正しいメールアドレスを入力してください'),
  contactPhone: yup
    .string()
    .optional()
    .test('phone-format', '正しい電話番号の形式で入力してください', function(value: string | undefined) {
      if (!value) return true;
      return /^[\d-+().\s]+$/.test(value);
    }),
});

type OrganizationInfo = yup.InferType<typeof schema>;

interface OrganizationInfoFormProps {
  onSubmit: (data: OrganizationInfo) => void;
  onBack: () => void;
  isLoading?: boolean;
}

// 月の選択肢
const monthOptions = [
  { value: 1, label: '1月' },
  { value: 2, label: '2月' },
  { value: 3, label: '3月' },
  { value: 4, label: '4月' },
  { value: 5, label: '5月' },
  { value: 6, label: '6月' },
  { value: 7, label: '7月' },
  { value: 8, label: '8月' },
  { value: 9, label: '9月' },
  { value: 10, label: '10月' },
  { value: 11, label: '11月' },
  { value: 12, label: '12月' },
];

export const OrganizationInfoForm: React.FC<OrganizationInfoFormProps> = ({
  onSubmit,
  onBack,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      fiscalYearStartMonth: 4, // デフォルトは4月
    },
  });

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h6" gutterBottom>
        マンション情報の入力
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        マンション管理組合の基本情報を入力してください。※は必須項目です。
      </Typography>

      <Stack spacing={2}>
        <TextField
          {...register('name')}
          fullWidth
          label="マンション名 ※"
          error={!!errors.name}
          helperText={errors.name?.message}
          placeholder="〇〇マンション"
          disabled={isLoading}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            {...register('postalCode')}
            label="郵便番号"
            error={!!errors.postalCode}
            helperText={errors.postalCode?.message}
            placeholder="123-4567"
            disabled={isLoading}
            sx={{ width: '200px' }}
          />

          <TextField
            {...register('address')}
            fullWidth
            label="住所"
            error={!!errors.address}
            helperText={errors.address?.message}
            placeholder="東京都〇〇区〇〇1-2-3"
            disabled={isLoading}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            {...register('totalUnits', { 
              setValueAs: (value) => value === '' ? undefined : Number(value)
            })}
            label="総戸数"
            type="number"
            error={!!errors.totalUnits}
            helperText={errors.totalUnits?.message}
            placeholder="50"
            disabled={isLoading}
            sx={{ width: '150px' }}
          />

          <FormControl sx={{ width: '200px' }} disabled={isLoading}>
            <InputLabel>会計年度開始月</InputLabel>
            <Controller
              name="fiscalYearStartMonth"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="会計年度開始月"
                  value={field.value || 4}
                >
                  {monthOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Box>

        <TextField
          {...register('managementCompany')}
          fullWidth
          label="管理会社名"
          error={!!errors.managementCompany}
          helperText={errors.managementCompany?.message}
          placeholder="〇〇管理株式会社"
          disabled={isLoading}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            {...register('establishedDate')}
            label="設立年月日"
            type="date"
            InputLabelProps={{ shrink: true }}
            error={!!errors.establishedDate}
            helperText={errors.establishedDate?.message}
            disabled={isLoading}
            sx={{ width: '200px' }}
          />

          <TextField
            {...register('contactPhone')}
            fullWidth
            label="連絡先電話番号"
            error={!!errors.contactPhone}
            helperText={errors.contactPhone?.message}
            placeholder="03-1234-5678"
            disabled={isLoading}
          />
        </Box>

        <TextField
          {...register('contactEmail')}
          fullWidth
          label="連絡先メールアドレス"
          type="email"
          error={!!errors.contactEmail}
          helperText={errors.contactEmail?.message}
          placeholder="contact@example.com"
          disabled={isLoading}
        />
      </Stack>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={onBack} disabled={isLoading}>
          戻る
        </Button>
        <Button type="submit" variant="contained" disabled={isLoading}>
          登録実行
        </Button>
      </Box>
    </Box>
  );
};